import {
  EdgeTransportInfoType,
  NodeConnection,
  RouteNodeType,
  RouteParentNodeDataType,
} from '@lp/features/RoutePlanner/types';
import { LegLocationTypeEnum } from '@/api/apis/route';
import { CHARACTER_WIDTH, DEFAULT_SCHEMA_HEIGHT, EDGE_WIDTH, ICON_BLOCK_WIDTH } from './consts';
import { TreeLeg, TreeNodeInfo } from './treeLayout';

export const LEG_GAP = 40;

const LOC_TYPE_TO_NODE_TYPE: Partial<Record<LegLocationTypeEnum, string>> = {
  [LegLocationTypeEnum.airport]: 'Airport',
  [LegLocationTypeEnum.hub]: 'Hub',
  [LegLocationTypeEnum.hospital]: 'Hospital',
  [LegLocationTypeEnum.txcenter]: 'TxCenter',
  [LegLocationTypeEnum.homeResidence]: 'HomeResidence',
};

export const locTypeToNodeType = (locationType: LegLocationTypeEnum): string =>
  LOC_TYPE_TO_NODE_TYPE[locationType] ?? 'Custom';

export const calcNodeWidth = (title: string): number => ICON_BLOCK_WIDTH + title.length * CHARACTER_WIDTH;

const buildParentNodeData = (node: TreeNodeInfo): RouteNodeType<RouteParentNodeDataType>['data'] => ({
  id: node.id,
  segmentId: '',
  segmentType: node.segmentType,
  nodeType: 'parent',
  title: node.title,
  organType: node.organType,
  childrenTop: 0,
  childrenBottom: 0,
  includeLocationIds: [],
  locationType: node.locationType,
  schemaType: 'main',
});

const buildUnconnectedNode = (nodeInfo: TreeNodeInfo, x: number, y: number, width: number): RouteNodeType => ({
  id: nodeInfo.id,
  type: locTypeToNodeType(nodeInfo.locationType),
  position: { x, y },
  width,
  data: buildParentNodeData(nodeInfo),
});

export const buildUnconnectedSchema = (
  legs: TreeLeg[],
): { nodes: RouteNodeType[]; edges: unknown[]; totalWidth: number } => {
  const nodeY = DEFAULT_SCHEMA_HEIGHT / 2;
  const nodes: RouteNodeType[] = [];
  const edges: unknown[] = [];
  let offsetX = 0;

  legs.forEach((leg) => {
    const startWidth = calcNodeWidth(leg.start.title);
    const endWidth = calcNodeWidth(leg.end.title);
    const endX = offsetX + startWidth + EDGE_WIDTH;

    nodes.push(
      buildUnconnectedNode(leg.start, offsetX, nodeY, startWidth),
      buildUnconnectedNode(leg.end, endX, nodeY, endWidth),
    );

    const edgeData: EdgeTransportInfoType | undefined = leg.transport
      ? {
          legType: leg.transport.legType,
          time: leg.transport.time,
          aircraft: leg.transport.aircraft as EdgeTransportInfoType['aircraft'],
          index: leg.transport.aircraftIndex,
          isReposition: leg.transport.isReposition,
        }
      : undefined;

    edges.push({
      id: `e-${leg.id}`,
      source: leg.start.id,
      target: leg.end.id,
      type: 'straightEdge',
      sourceHandle: NodeConnection.fromMainRight,
      targetHandle: NodeConnection.toMainLeft,
      data: edgeData,
    });

    offsetX += startWidth + EDGE_WIDTH + endWidth + LEG_GAP;
  });

  return { nodes, edges, totalWidth: Math.max(0, offsetX - LEG_GAP) };
};
